import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { Observable, catchError, throwError } from 'rxjs';

import {
  LoggerService,
  extractErrorCode,
  resolveI18nKey,
} from '../../core/logging';
import { carriesDeletionBlockers } from '../models/deletion-blocker.model';
import { NotificationService } from '../service/notification.service';

const SKIP_LOG_FOR_PATHS: readonly string[] = [
  '/api/auth/me',
  '/api/auth/password-signin',
  '/api/auth/2fa',
  '/api/users/me/two-factor/',
  '/api/auth/magic-link-verify',
  '/api/auth/send-magic-link',
  '/api/auth/signup',
  '/api/auth/request-password-reset',
  '/api/auth/verify-password-reset',
  '/api/auth/reset-password',
  '/api/auth/heartbeat',
  '/api/security-alert',
];

const SKIP_LOG_FOR_REQUESTS: readonly { method: string; path: string }[] = [
  { method: 'POST', path: '/api/users/me/deactivate' },
  { method: 'DELETE', path: '/api/users/me' },
  { method: 'DELETE', path: '/api/tenants/me' },
];

function isExpectedClientError(status: number): boolean {
  return status >= 400 && status < 500;
}

function isSkipped(method: string, url: string, status: number): boolean {
  if (!isExpectedClientError(status)) {
    return false;
  }

  return (
    SKIP_LOG_FOR_PATHS.some((path) => url.includes(path)) ||
    SKIP_LOG_FOR_REQUESTS.some(
      (entry) => entry.method === method && url.endsWith(entry.path),
    )
  );
}

export const ErrorLoggingInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggerService);
  const notify = inject(NotificationService);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        return throwError(() => error);
      }

      const errorCode = extractErrorCode(error);

      const meta = {
        module: 'http',
        method: request.method,
        url: request.url,
        status: error.status,
        errorCode,
      };

      if (carriesDeletionBlockers(error)) {
        logger.debug(
          `Blocked operation ${request.method} ${request.url} (status ${error.status})`,
          meta,
        );
        return throwError(() => error);
      }

      const skipped = isSkipped(request.method, request.url, error.status);

      if (skipped) {
        logger.debug(
          `Suppressed HTTP error for ${request.method} ${request.url} (status ${error.status})`,
          meta,
        );
        return throwError(() => error);
      }

      const i18nKey = resolveI18nKey(errorCode, error.status);

      if (error.status === 0 || error.status === -1) {
        logger.warn(
          `Network error ${request.method} ${request.url}`,
          error,
          meta,
        );
        notify.warn(i18nKey);
      } else if (error.status >= 500) {
        logger.error(
          `HTTP ${error.status} ${request.method} ${request.url}`,
          error,
          meta,
        );
        notify.error(i18nKey);
      } else if (error.status === 403) {
        logger.warn(
          `HTTP 403 ${request.method} ${request.url}`,
          error,
          meta,
        );
        notify.warn(i18nKey);
      } else if (error.status === 429) {
        logger.warn(
          `HTTP 429 ${request.method} ${request.url}`,
          error,
          meta,
        );
        notify.warn(i18nKey);
      } else if (error.status >= 400) {
        logger.warn(
          `HTTP ${error.status} ${request.method} ${request.url}`,
          error,
          meta,
        );
        notify.warn(i18nKey);
      }
      return throwError(() => error);
    }),
  );
};
